import { collection } from '@/infra/collection'
import { mapQuestionResourceToQuestion } from '@/features/jobs/questions/question-mappers'
import type { CursorPaged } from '@/infra/paging'
import { OCRScopeWork, type OCRScopeWorkEvent } from './OCRScopeWork'
import {
  mapAggregationFieldResourceToAggregation,
  mapManualInputResourceToAnswer,
  mapOCRScopeWorkResourceToProps,
  mapStatusToStatusNameResource,
} from './ocr-scope-work-mappers'
import { type WunderApi } from '@/features/jobs/api/wunder-api'
import type { RealtimeMessage } from '@/features/jobs/api/resources/realtime-messages'
import type { OCRScopeWorkResource } from '@/features/jobs/api/resources/ocr-scope-work'
import { EMPTY, map, type Observable, share } from 'rxjs'
import { env } from '@/infra/env'
import { Id } from '@/infra/id'
import type { OCRManualInputs } from './ocr-manual-input'
import type { OCRScopeWorkAggregation } from './ocr-scope-work-aggregation'
import type { OCRScopeWorkStatus } from './ocr-scope-work-status'
import type { PublishedLegendStore } from '@/features/jobs/legends/PublishedLegendStore'
import type {
  LegendTemplate,
  LegendVersionId,
} from '@/features/jobs/legends/PublishedLegend'

/**
 * Loads, caches and changes scope works.
 */
export class OCRScopeWorkStore {
  /**
   * The collection.
   */
  readonly ocrScopeWorks = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new OCRScopeWork(),
  })

  /**
   * Realtime changes to scope works, after they were synced to the collection.
   */
  readonly events$: Observable<OCRScopeWorkEvent> = EMPTY

  constructor(
    private readonly _api: WunderApi,
    private readonly _publishedLegendStore: PublishedLegendStore,
  ) {
    // The Wunder API is optional in development, so its events may be off.
    if (process.env.NODE_ENV === 'development' && !env.WUNDER_API_EVENTS) {
      return
    }

    this.events$ = this._api.scopeWork.events$.pipe(
      map((message) => this._receiveRealtimeMessage(message)),
      share(),
    )
    this.events$.subscribe()
  }

  /**
   * Fetches a non-archived scope work, or `null` when there is none.
   */
  async fetchScopeWork({
    workspaceId,
    scopeWorkId,
    signal,
  }: FetchScopeWorkQuery): Promise<OCRScopeWork | null> {
    const page = await this._api.scopeWork.search(
      {
        limit: 1,
        workspace_id: workspaceId ?? '',
        search_text: '',
        aggregations: `archived:false,id:${Id.assert(scopeWorkId)}`,
      },
      signal,
    )

    return page.data.length === 0
      ? null
      : this.ocrScopeWorks.set(mapOCRScopeWorkResourceToProps(page.data[0]))
  }

  /**
   * Lists the non-archived scope works.
   */
  async listScopeWorks({
    workspaceId,
    statuses,
    searchText,
    limit,
    after,
    signal,
  }: ListScopeWorksQuery): Promise<CursorPaged<OCRScopeWork>> {
    const page = await this._api.scopeWork.search(
      {
        limit,
        aggregations: aggregationsFor(statuses),
        search_text: searchText,
        workspace_id: workspaceId ?? '',
        after: after ?? '',
      },
      signal,
    )

    return {
      data: this.ocrScopeWorks.set(
        page.data.map(mapOCRScopeWorkResourceToProps),
      ),
      next: page.cursor,
    }
  }

  /**
   * Fetches the counts of scope works per field value.
   */
  async fetchAggregations({
    workspaceId,
    signal,
  }: FetchAggregationsQuery): Promise<OCRScopeWorkAggregation[]> {
    const aggregations = await this._api.scopeWork.getAggregations(
      workspaceId ?? '',
      signal,
    )
    return aggregations.map(mapAggregationFieldResourceToAggregation)
  }

  /**
   * Fetches the templates of the legend version, as supported by scope work.
   */
  async fetchLegendTemplates(
    legendVersionId: LegendVersionId,
    signal?: AbortSignal,
  ): Promise<LegendTemplate[]> {
    const legendVersion =
      await this._publishedLegendStore.fetchSupportedLegendVersion(
        legendVersionId,
        signal,
      )
    return legendVersion.templates
  }

  /**
   * Fetches the questions to answer when scoping work with the template.
   */
  async fetchManualInputs({
    workspaceId,
    legendVersionId,
    templateId,
    signal,
  }: FetchManualInputsQuery): Promise<OCRManualInputs> {
    const result = await this._api.scopeWork.getManualInputs(
      {
        workspace_id: Id.assert(workspaceId),
        legend_id: legendVersionId.id,
        legend_version: String(legendVersionId.version),
        template_id: Id.assert(templateId),
      },
      signal,
    )
    const manualInputs = result?.manual_inputs ?? []
    return {
      questions: manualInputs.map(mapQuestionResourceToQuestion),
      answers: manualInputs.map(mapManualInputResourceToAnswer),
    }
  }

  /**
   * Starts scoping a job from each of the files.
   */
  async createScopeWork({
    workspaceId,
    userPublicId,
    supportingClientUserPublicId,
    teamId,
    legendVersionId,
    templateId,
    answers,
    files,
  }: CreateScopeWorkOptions) {
    await this._api.scopeWork.create({
      workspace_id: Id.assert(workspaceId),
      user_id: Id.assert(userPublicId),
      supporting_client_user_public_id: supportingClientUserPublicId,
      team_id: teamId,
      legend_id: legendVersionId.id,
      legend_version: legendVersionId.version,
      template_id: Id.assert(templateId),
      data: answers,
      files,
    })
  }

  /**
   * Archives the scope work.
   */
  async archive(scopeWorkId: Id) {
    await this._api.scopeWork.archive(Id.assert(scopeWorkId))
  }

  /**
   * Runs OCR for the scope work again.
   */
  async rerun(scopeWorkId: Id) {
    await this._api.scopeWork.rerun(Id.assert(scopeWorkId))
  }

  /**
   * Links the scope work to a job that was created by hand.
   */
  async linkManually({ scopeWorkId, jobId }: LinkManuallyOptions) {
    await this._api.scopeWork.linkManually(Id.assert(scopeWorkId), {
      job_id: Id.assert(jobId),
    })
  }

  /**
   * Marks the scope work as reviewed, making it ready to submit.
   */
  async review(scopeWorkId: Id) {
    await this._api.scopeWork.review(Id.assert(scopeWorkId))
  }

  private _receiveRealtimeMessage(message: RealtimeMessage): OCRScopeWorkEvent {
    switch (message.type) {
      case 'ScopeJobCreated':
        return { type: 'Created', scopeWork: this._set(message.data) }
      case 'ScopeJobUpdated':
        return { type: 'Updated', scopeWork: this._set(message.data) }
      case 'ScopeJobNeedsAttention':
        return { type: 'NeedsAttention', scopeWork: this._set(message.data) }
      case 'ScopeJobErrored':
        return { type: 'Errored', scopeWork: this._set(message.data) }
      case 'ScopeJobSubmitted':
        return { type: 'Submitted', scopeWork: this._set(message.data) }
      case 'ScopeJobArchived': {
        const scopeWorkId = Id.assert(message.data.id)
        this.ocrScopeWorks.remove(scopeWorkId)
        return { type: 'Archived', scopeWorkId }
      }
    }
  }

  private _set(resource: OCRScopeWorkResource) {
    return this.ocrScopeWorks.set(mapOCRScopeWorkResourceToProps(resource))
  }
}

/**
 * Builds the search aggregations that filter non-archived scope works by
 * status.
 */
function aggregationsFor(statuses: OCRScopeWorkStatus[] | undefined) {
  const parts = ['archived:false']
  if (statuses) {
    parts.push(
      `status_name:${statuses.map(mapStatusToStatusNameResource).join(';')}`,
    )
  }
  return parts.join(',')
}

/** @see {OCRScopeWorkStore.fetchScopeWork} */
export interface FetchScopeWorkQuery {
  /**
   * The workspace to look in, or {@link Id.none} for all workspaces.
   */
  workspaceId: Id
  scopeWorkId: Id
  signal?: AbortSignal
}

/** @see {OCRScopeWorkStore.listScopeWorks} */
export interface ListScopeWorksQuery {
  /**
   * The workspace to look in, or {@link Id.none} for all workspaces.
   */
  workspaceId: Id
  /**
   * Statuses to filter by, or `undefined` for any status.
   */
  statuses?: OCRScopeWorkStatus[]
  searchText: string
  limit: number
  after?: string | null
  signal?: AbortSignal
}

/** @see {OCRScopeWorkStore.fetchAggregations} */
export interface FetchAggregationsQuery {
  /**
   * The workspace to count in, or {@link Id.none} for all workspaces.
   */
  workspaceId: Id
  signal?: AbortSignal
}

/** @see {OCRScopeWorkStore.fetchManualInputs} */
export interface FetchManualInputsQuery {
  workspaceId: Id
  legendVersionId: LegendVersionId
  templateId: Id
  signal?: AbortSignal
}

/** @see {OCRScopeWorkStore.createScopeWork} */
export interface CreateScopeWorkOptions {
  workspaceId: Id
  userPublicId: Id
  supportingClientUserPublicId?: string
  teamId?: string
  legendVersionId: LegendVersionId
  templateId: Id
  /**
   * Answers to the manual input questions, keyed by question ID.
   */
  answers: Record<string, unknown>
  files: File[]
}

/** @see {OCRScopeWorkStore.linkManually} */
export interface LinkManuallyOptions {
  scopeWorkId: Id
  jobId: Id
}
